import {
  patchAddPricingInsurancesByPricingSpecId,
  PaymentMethodType,
  postCreatePricing,
  RangeType,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { PricingCreateFormValues } from '@/components/modules/PricingCreate/types/PricingCreateFormValues';
import { ProductSelectOption } from '@/components/modules/PricingCreate/types/ProductSelectOption';
import { ProgramSelectOption } from '@/components/modules/PricingCreate/types/ProgramSelectOption';
import { usePricingEditor } from '@/components/modules/PricingEditor/hooks/usePricingEditor';
import { PricingSpecRequestMap } from '@/components/modules/PricingEditor/maps/pricingSpecRequest.map';
import { PricingSpecFormValues } from '@/components/modules/PricingEditor/types/PricingSpecFormValues';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';
import { useRequest } from '@/hooks/requests/useRequest';

export const usePricingCreate = () => {
  const {
    actions: { setSnackbarNotification },
  } = useNotificationsContext();
  const { request } = useRequest();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [pricingId] = useState<string>(uuidv4());
  const [requestLoading, setRequestLoading] = useState(false);

  const [pricingDataForm, setPricingDataForm] = useState<PricingCreateFormValues>({
    name: '',
    description: '',
    product: ProductSelectOption.CONSUMER_LOAN,
    program: ProgramSelectOption.FREE_PROGRAM,
    paymentMethodType: PaymentMethodType.BNPL_INSTALMENTS,
  });

  const { getOrCreatePricingSpec } = usePricingEditor(pricingId);

  const onNext = async (
    data: PricingCreateFormValues & PricingSpecFormValues,
    pricingSpecSearchEnabled: boolean = true,
  ) => {
    const allFormData = { ...pricingDataForm, ...data };

    if (step === 1) {
      setPricingDataForm(data as PricingCreateFormValues);
      setStep(2);
    }
    if (step === 2) {
      await createPricingSpecAndPricing(allFormData, pricingSpecSearchEnabled);
      navigate(`/pricing/${pricingId}`);
    }
  };

  const onPrev = () => {
    if (step === 2) {
      setStep(1);
    }
  };

  const createPricingSpecAndPricing = async (
    data: PricingSpecFormValues & PricingCreateFormValues,
    pricingSpecSearchEnabled: boolean = true,
  ) => {
    try {
      setRequestLoading(true);
      const pricingSpecRequest = PricingSpecRequestMap.fromFormValues(
        uuidv4(),
        data.product,
        data.program,
        data.paymentMethodType,
        data,
      );

      const newPricingsSpec = await getOrCreatePricingSpec(pricingSpecRequest, pricingSpecSearchEnabled);
      const newPricingSpecRequest = newPricingsSpec.request;

      await request(postCreatePricing, {
        id: pricingId,
        name: data.name,
        description: data.description as string,
        rangeType: data.program === ProgramSelectOption.ZERO_PROGRAM ? RangeType.PERIOD : RangeType.PRINCIPAL,
        pricingSpecIds: [newPricingSpecRequest.id],
      });

      if (newPricingsSpec.availableInsurancesIds && newPricingsSpec.availableInsurancesIds.length > 0) {
        await request(patchAddPricingInsurancesByPricingSpecId, {
          id: newPricingSpecRequest.id,
          insuranceIds: newPricingsSpec.availableInsurancesIds,
        });
      }
    } catch (err) {
      setSnackbarNotification({
        msg: 'REQUEST_ERROR',
        level: 'error',
        error: err,
      });
      throw err;
    } finally {
      setRequestLoading(false);
    }
  };

  return { step, onNext, onPrev, pricingDataForm, requestLoading };
};
